/**
 * MentionList — dropdown UI for the @-IntelliSense.
 *
 * Rendered by mentionSuggestion's ReactRenderer above the @-cursor.
 * Shows the matching notify_authors as a clickable list. Arrow keys
 * navigate, Enter selects.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import type { MentionItem } from './mentionSuggestion'

interface MentionListProps {
  items: MentionItem[]
  command: (attrs: { id: string; label: string }) => void
}

export interface MentionListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean
}

export const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  function MentionList({ items, command }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0)

    // Reset selection when items change (new query)
    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    const selectItem = (index: number) => {
      const item = items[index]
      if (!item) return
      command({ id: item.id, label: item.id })
    }

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setSelectedIndex((i) => (i + 1) % Math.max(items.length, 1))
          return true
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setSelectedIndex((i) =>
            i <= 0 ? Math.max(items.length - 1, 0) : i - 1
          )
          return true
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault()
          selectItem(selectedIndex)
          return true
        }
        return false
      },
    }))

    if (items.length === 0) {
      return (
        <div
          style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 13,
            color: '#888',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            minWidth: 180,
          }}
        >
          Keine Treffer
        </div>
      )
    }

    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          minWidth: 200,
          maxHeight: 240,
          overflowY: 'auto',
          padding: 4,
        }}
      >
        {items.map((item, index) => {
          const isSelected = index === selectedIndex
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => selectItem(index)}
              onMouseEnter={() => setSelectedIndex(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '6px 8px',
                background: isSelected ? '#eef' : 'transparent',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 13,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: item.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1 }}>{item.name}</span>
            </button>
          )
        })}
      </div>
    )
  }
)
